import type { VisitRecord } from '.';
import features from '../../ruleset/features';

/**
 * Check if player has met unlock criteria for Visit feature
 * @returns
 */
export const hasMetUnlockCriteria = (state: VisitRecord.State) =>
  // After reborn friend visit should be unlocked
  state.petRaised >= 1 ||
  // Unlocked if pet has evolved at least once.
  state.pet.evolutions.length > 1;

/**
 * Check if feature is unlocked
 * @returns
 */
export const isUnlocked = (state: VisitRecord.State) => {
  if (!features(state).visitFriend) {
    // Feature is disabled
    return false;
  }

  return hasMetUnlockCriteria(state);
};

/**
 * Get count of times player has visited a friend
 * @param state Player state
 * @param friendId the friend visited by the player
 * @return the visit count
 */
export const getVisitsCount = (state: VisitRecord.State, friendId: string) =>
  state.visit.visits[friendId]?.count ?? 0;

/**
 * Gets the timestamp of the most recent time you visited a friend
 * @param state Player state
 * @param [friendId] The friend to check when we visited.
 *                   If omitted, checks all visited friends
 */
export const getLastVisitTimestamp = (
  state: VisitRecord.State,
  friendId?: string,
) => {
  const { visits } = state.visit;
  if (friendId == null) {
    friendId = Object.keys(visits).sort(
      (a, b) => visits[b].lastUpdatedAt - visits[a].lastUpdatedAt,
    )[0];
  }
  return visits[friendId]?.lastUpdatedAt;
};

/**
 * Get count of times player has been visited by a friend
 * @param state Player state
 * @param friendId the friend visiting the player
 * @return the visited count
 */
export const getVisitedCount = (state: VisitRecord.State, friendId: string) =>
  state.visit.visited[friendId]?.count ?? 0;

/**
 * Check if player has had a visitor since their last session
 * @return true if another player has visited, false otherwise
 */
export const wasVisitedSinceLastSession = (state: VisitRecord.State) =>
  Object.values(state.visit.visited).some(
    (visitor) => visitor.lastUpdatedAt > state.updatedAt,
  );

/**
 * Gets the details of the most recent visitor
 * @return id and display name of the most recent visitor
 */
export const getMostRecentVisitor = (state: VisitRecord.State) =>
  Object.entries(state.visit.visited)
    .sort(([_, a], [__, b]) => a.lastUpdatedAt - b.lastUpdatedAt)
    .slice(-1)
    .map(([id, { name }]) => ({ id, name }))
    .pop();
